/* ============================================================================
   TabBar — Build 9 Premium Reskin.
   Surface raised pill + accent for selected tab + text-tertiary unselected.
   ============================================================================ */
import type { ComponentType } from "react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { User, Users, Calendar } from "lucide-react";
import {
  BOTTOM_SLOT_EVENT,
  BOTTOM_SLOT_OPTIONS,
  bottomSlotOption,
  readBottomSlot,
  setBottomSlot,
} from "@/lib/bottomSlotPref";
import { FriendPickerDialog } from "@/components/FriendPickerDialog";
import { useSession, hasProAccess } from "./SessionProvider";
import { useAuth } from "./AuthProvider";
import { useKeyboardVisible } from "@/hooks/useKeyboardVisible";
import { HeroDexButton } from "./HeroDexButton";
import {
  BAR_CONTENT_HEIGHT_PX,
  BAR_BOTTOM_LIFT_PX,
  BAR_TOP_RISE_PX,
  CENTER_PLACEHOLDER_WIDTH_PX,
  ITEM_BOTTOM_INSET_FRACTION,
  TAB_LABEL_TRACKING_EM,
} from "./tabbarLayout";
import { confirmPageExit } from "@/lib/pageExitGuard";
import { getPlatform } from "@/lib/platform";

/**
 * Android sits the bar ABOVE the system navigation bar (2026-09-30). iPhone's
 * home indicator is a thin see-through line the bar is designed to run under
 * (BAR_BOTTOM_LIFT_PX, above). Android's three-button bar is opaque and
 * tappable, and once the app went edge-to-edge on every Android version (Play
 * "deprecated edge-to-edge APIs") the back / home / recents buttons sat on
 * FRIENDS and POKEMON. The gesture pill on Android 16 phones gets the same
 * clearance, as every Android app gives it.
 */
const ANDROID_BOTTOM_PAD = "var(--nx-safe-bottom)";

interface TabDef {
  href: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number | string }>;
  label: string;
  testid: string;
  // Marks a tab whose surface is Pro. The tab stays NAVIGABLE for everyone —
  // the gate is tease-then-gate inside the page itself, not at the tab. All a
  // `pro` tab does here is wear a green PRO pill while the user lacks access,
  // so the upsell reads as a signpost rather than a locked door.
  pro?: boolean;
  showNew?: boolean;
  /** Friend-scoped slot option: tap opens the friend picker, not a route. */
  picker?: "compare" | "trade";
  /** Active matcher for picker tabs (their route embeds a friend id). */
  activePattern?: RegExp;
  /** The user-choice slot: long-press opens the slot chooser. */
  slotChooser?: boolean;
}

// b18d item 3: the centre "Dex" slot is no longer a plain tab — it's the raised
// HeroDexButton (see below). The four flanking tabs stay plain; they arc around
// the hero, two on each side, for a control-panel layout.
const LEFT_TABS: TabDef[] = [
  { href: "/",        icon: User,  label: "Profile", testid: "tab-profile" },
  { href: "/friends", icon: Users, label: "Friends", testid: "tab-friends", showNew: true },
];

/**
 * The fourth slot is the TRAINER'S CHOICE, not ours.
 *
 * It has changed hands twice by code edit (Search, Raid, Search again), each
 * time on an argument about whose need was bigger. Defaulting it to Meta and
 * letting Settings change it ends that argument: the bar carries what that
 * trainer uses. See bottomSlotPref for the registry and for how to add another
 * option.
 */
function rightTabs(slotKey: string): TabDef[] {
  const slot = bottomSlotOption(slotKey);
  return [
    {
      href: slot.href,
      icon: slot.icon,
      label: slot.label,
      testid: slot.testid,
      picker: slot.picker,
      activePattern: slot.activePattern,
      slotChooser: true,
    },
    { href: "/calendar", icon: Calendar, label: "Calendar", testid: "tab-calendar" },
  ];
}

// Single source of truth for the centre hero's route (was the old "/dex" tab).
const DEX_HREF = "/dex";

export function TabBar() {
  const [loc, setLocation] = useLocation();
  const { tier, tradeBadgeSeen, markTradeBadgeSeen } = useSession();

  // Slot chooser (2026-08-27 ask): LONG-PRESS on the fourth tab opens a small
  // menu of every slot option, so swapping the tab doesn't need a Settings
  // trip. A completed long-press suppresses the tap that ends it (the same
  // pointer sequence would otherwise also navigate).
  const [chooserOpen, setChooserOpen] = useState(false);
  const chooserTimer = useRef<number | null>(null);
  const suppressTapRef = useRef(false);
  const startChooserPress = () => {
    suppressTapRef.current = false;
    chooserTimer.current = window.setTimeout(() => {
      chooserTimer.current = null;
      suppressTapRef.current = true;
      setChooserOpen(true);
    }, 450);
  };
  const cancelChooserPress = () => {
    if (chooserTimer.current != null) {
      window.clearTimeout(chooserTimer.current);
      chooserTimer.current = null;
    }
  };
  // Closing the chooser must also clear the tap suppression: the flag exists
  // to swallow the click that ENDS the long-press, but when the next tap goes
  // to a chooser row or the backdrop instead, the flag would linger and
  // silently eat the trainer's next ordinary tap on the tab.
  const closeChooser = () => {
    setChooserOpen(false);
    suppressTapRef.current = false;
  };

  // Friend picker for the Compare / Trade slot options: their destinations are
  // friend-scoped routes, so the tab asks "which friend?" exactly like the
  // main-menu rows do (same dialog, same copy). Mounted only while open — the
  // picker fetches the friends list, and nothing should fetch until asked.
  const [friendPicker, setFriendPicker] = useState<null | "compare" | "trade">(null);
  const { account } = useAuth();

  // Global fix: while the soft keyboard is open, slide the bottom nav off the
  // bottom edge so it never floats above the keyboard. The bar is position:
  // fixed (out of normal flow), so translating it away leaves no layout gap —
  // page content keeps its pb-tabbar padding. Returns when the keyboard hides.
  const keyboardOpen = useKeyboardVisible();

  // Re-read on change so switching the slot in Settings updates the bar
  // immediately rather than on the next cold start.
  const [slotKey, setSlotKey] = useState(readBottomSlot);
  useEffect(() => {
    const sync = () => setSlotKey(readBottomSlot());
    window.addEventListener(BOTTOM_SLOT_EVENT, sync);
    return () => window.removeEventListener(BOTTOM_SLOT_EVENT, sync);
  }, []);
  const SIDE_TABS = { left: LEFT_TABS, right: rightTabs(slotKey) };

  // b14: live pending friend-request count → numeric badge on the Friends tab.
  // Polls quietly so a request that lands while the app is open surfaces
  // without a manual refresh.
  const pendingQ = useQuery<{ pending: unknown[] }>({
    queryKey: ["/api/friends/pending"],
    enabled: Boolean(account),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const pendingCount = account ? (pendingQ.data?.pending?.length ?? 0) : 0;

  return (
    <>
      {/* b13e B3 → b17x-ui: flush, edge-to-edge bar pinned to the bottom of the
          viewport. No floating rounded island, no fog fade. b17x-ui: the nav no
          longer pads itself by var(--nx-safe-bottom); instead the bar
          background runs all the way to the physical bottom edge so the raised
          medallion can sit flush on the home-indicator band (it reads as a
          control button, intentionally crossing the safe area). The safe-area
          inset is moved onto the FLANKING tab columns only, so PROFILE / FRIENDS
          / TRADE / CALENDAR stay above the home indicator and are never
          occluded. b13e B2: all colours come from theme tokens
          (--surface-raised / --border-subtle), so the bar tracks light/dark.
          b17y-ui: b17x-ui pushed the bar too far down, leaving the medallion
          deep in the safe-area band and the flanking tabs sitting too low. The
          nav now reclaims half the home-indicator inset as its own bottom
          padding, lifting the whole bar up by ~17px on devices with an
          indicator (~34px inset) while the medallion still extends below the
          safe-area line, just less aggressively than before.
          E44: the bar still sat too low (label row near the very bottom edge,
          well below the coin's centre). We added a fixed BAR_BOTTOM_LIFT_PX on
          top of the half-inset so the whole bar (background floor + label row)
          rose toward the coin's vertical midline.
          E48: the user now wants the round coin's BOTTOM edge to touch the
          physical bottom of the screen. The nav's bottom padding was what held
          the bar floor (and the coin anchored bottom:0 to it) UP off the edge,
          so we remove BOTH the E44 lift (now BAR_BOTTOM_LIFT_PX = 0) AND the
          nav-level half-inset here, dropping the bar floor to the physical
          bottom; the coin then overflows down to the screen edge. The four
          LABELS keep their own clearance above the home indicator via each
          item's ITEM_BOTTOM_INSET_FRACTION padding (below), so dropping the
          nav-level inset does not push the labels onto the indicator. The bar
          HEIGHT (BAR_CONTENT_HEIGHT_PX) and the coin-to-items offset
          (HERO_TOP_OFFSET_PX) are unchanged; only the vertical offset drops.
          E52: the white panel itself was too SHORT at the top - its top edge sat
          around the coin's lower portion, so the coin floated well above a short
          bar instead of sitting half-in a solid panel. We raise ONLY the panel's
          TOP edge up to the coin's vertical midline (the yellow-line reference in
          IMG_0859) via BAR_TOP_RISE_PX as paddingTop here. Because the nav is
          fixed bottom-0, adding paddingTop grows the white background UPWARD (the
          top edge rises by BAR_TOP_RISE_PX) while the item row, laid out after the
          padding, keeps its exact bottom placement: row bottom stays at the
          screen bottom, so PROFILE / FRIENDS / TRADE / CALENDAR and the coin do
          NOT move. BAR_BOTTOM_LIFT_PX stays 0 (bottom still pinned to the edge);
          only the top rises. */}
      <nav
        className={[
          "tabbar-3d fixed bottom-0 inset-x-0 z-50 bg-[var(--surface-raised)] border-t border-[var(--border-subtle)]",
          "transition-transform [transition-duration:var(--duration-fast)] ease-out",
          keyboardOpen ? "translate-y-full pointer-events-none" : "translate-y-0",
        ].join(" ")}
        data-testid="tab-bar"
        data-keyboard-hidden={keyboardOpen ? "true" : undefined}
        aria-hidden={keyboardOpen ? "true" : undefined}
        aria-label="Primary"
        style={{
          paddingTop: `${BAR_TOP_RISE_PX}px`,
          paddingBottom:
            getPlatform() === "android"
              ? `calc(${BAR_BOTTOM_LIFT_PX}px + ${ANDROID_BOTTOM_PAD})`
              : `${BAR_BOTTOM_LIFT_PX}px`,
        }}
      >
        {/* b19-ui (E31): the tab row is now FULL screen width (edge-to-edge),
            not capped to max-w-md. The five primary slots (Profile, Friends,
            [reserved centre], Trade, Calendar) are equal-basis flex columns
            (flex-1 basis-0) so they distribute evenly across whatever the device
            width is (iPhone SE / 13 / Pro Max) with no overflow or clipped
            label. The raised HeroDexButton is taken OUT of the flow and
            absolutely centred over the reserved middle slot, so its oversized
            ~120px coin overflows symmetrically above the bar without stealing
            horizontal space from its neighbours (previously the intrinsic coin
            column squeezed the side flex-1 columns and clipped CALENDAR off the
            right edge on 390pt devices). Side safe-area insets keep the outermost
            slots clear of rounded-corner / notch landscape insets. */}
        <div
          className="relative flex items-stretch w-full"
          style={{
            paddingLeft: "var(--nx-safe-left)",
            paddingRight: "var(--nx-safe-right)",
          }}
        >
          {/* Left two slots. Each is an equal-basis flex column; items centre
              vertically within the fixed (E35: 20% shorter) bar content height
              and carry the home-indicator safe-area floor so icons stay above
              the indicator. */}
          {SIDE_TABS.left.map((t) => renderTab(t))}

          {/* E37/E42: centre placeholder is a FIXED-WIDTH slot
              (CENTER_PLACEHOLDER_WIDTH_PX) and NOT a flex-1 column. E42 derives
              that width from the coin diameter plus clearance, so it is always
              at least as wide as the coin; the four real items keep flex-1
              basis-0 and share only the REMAINING width, which pushes Friends
              (slot 2) and Trade (slot 4) outward far enough to clear the coin's
              footprint at any viewport (E37's 86px slot was narrower than the
              96px coin, so those labels still sat under it). shrink-0 keeps the
              fixed width on narrow devices. It is aria-hidden + pointer-events-
              none so it is invisible and never captures taps; the hero coin sits
              over it and overflows upward without affecting layout width. */}
          <div
            className="shrink-0 pointer-events-none"
            style={{ width: `${CENTER_PLACEHOLDER_WIDTH_PX}px` }}
            data-testid="tab-center-placeholder"
            aria-hidden
          />

          {/* Right two slots: mirror of the left. */}
          {SIDE_TABS.right.map((t) => renderTab(t))}

          {/* b18d item 3: raised hero — tap → /dex (unchanged routing),
              long-press → quick-action panel into the existing live-scan flow.
              b19-ui: absolutely centred over the reserved middle slot so the coin
              never occupies row width; it crosses the home indicator on purpose
              (no safe-area padding) and stays on top via z-10. */}
          <div className="absolute inset-x-0 bottom-0 flex justify-center pointer-events-none z-10">
            <div className="relative flex flex-col items-center justify-end pointer-events-auto">
              <HeroDexButton href={DEX_HREF} active={loc.startsWith(DEX_HREF)} />
            </div>
          </div>
        </div>
      </nav>

      {/* Slot chooser: the long-press menu over the fourth tab. Every registry
          option, current one ticked; picking swaps the tab in place (and
          persists via bottomSlotPref) without navigating anywhere. */}
      {chooserOpen && (
        <>
          <div
            className="fixed inset-0 z-[59]"
            onClick={closeChooser}
            data-testid="slot-chooser-backdrop"
            aria-hidden
          />
          {/* 2026-08-28 tighten: no title, no footer hint — the options ARE
              the explanation, and the HIGHLIGHT on the current one says "this
              is what the tab shows now". Centred rows, narrow card, so the
              menu reads as a small picker rising from the button it changes. */}
          <div
            className="fixed z-[60] right-2 rounded-2xl overflow-hidden p-1"
            role="menu"
            aria-label="Choose the bottom bar shortcut"
            data-testid="slot-chooser"
            style={{
              bottom: `calc(var(--nx-safe-bottom) + ${BAR_CONTENT_HEIGHT_PX + BAR_TOP_RISE_PX + 10}px)`,
              background: "var(--surface-overlay)",
              border: "1px solid var(--border-subtle)",
              boxShadow: "0 12px 40px rgba(0, 0, 0, 0.3)",
              minWidth: 148,
            }}
          >
            {BOTTOM_SLOT_OPTIONS.map((opt) => {
              const OptIcon = opt.icon;
              const current = opt.key === slotKey;
              return (
                <button
                  key={opt.key}
                  type="button"
                  role="menuitemradio"
                  aria-checked={current}
                  data-testid={`slot-chooser-${opt.key}`}
                  onClick={() => {
                    setBottomSlot(opt.key);
                    closeChooser();
                  }}
                  className="w-full px-3 py-2.5 rounded-xl flex items-center justify-center gap-2 text-[13px] font-semibold hover-elevate"
                  style={
                    current
                      ? { color: "var(--accent-fill)", background: "var(--accent-subtle)" }
                      : { color: "var(--text-primary)" }
                  }
                >
                  <OptIcon className="w-4 h-4 shrink-0" />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Friend picker for the Compare / Trade slot tabs — mounted only while
          open, same dialog + copy as the main-menu rows (MainMenu.tsx). */}
      {friendPicker != null && (
        <FriendPickerDialog
          open
          onOpenChange={(v) => setFriendPicker(v ? friendPicker : null)}
          title={friendPicker === "trade" ? "Trade with which friend?" : "Compare with which friend?"}
          description={
            friendPicker === "trade"
              ? "Their trade list, your leads, and what the two of you could swap."
              : "Head to head across every dex, and how you're both tracking."
          }
          onPick={(accountId) => {
            setFriendPicker(null);
            if (!confirmPageExit()) return;
            setLocation(`/friends/${accountId}/${friendPicker === "trade" ? "trades" : "compare"}`);
          }}
        />
      )}
    </>
  );

  function renderTab({ href, icon: Icon, label, testid, pro, showNew, picker, activePattern, slotChooser }: TabDef) {
            let active = activePattern
              ? activePattern.test(loc)
              : href === "/"
                ? loc === "/"
                : href !== "" && loc.startsWith(href);
            // The Compare/Trade slot landing routes live UNDER /friends
            // (/friends/:id/compare|trades), so the Friends tab's prefix match
            // also lit up beside the slot tab (device report 2026-08-27). When
            // the chosen slot claims the current route, Friends yields; with a
            // non-claiming slot chosen, those routes still light Friends, the
            // only related tab on the bar.
            if (href === "/friends" && active) {
              const slotOpt = bottomSlotOption(slotKey);
              if (slotOpt.activePattern?.test(loc)) active = false;
            }
            // Green PRO pill on a Pro surface the user hasn't unlocked. This is
            // signage only — it never blocks the tap (see TabDef.pro).
            const showProPill = Boolean(pro) && !hasProAccess(tier);
            // b14: a live pending-request count on the Friends tab wins over the
            // generic "NEW" hint. The numeric badge shows even when the tab is
            // active so the trainer always knows how many invites await.
            const friendCount = href === "/friends" ? pendingCount : 0;
            const showCount = friendCount > 0;
            const showBadge = showNew && !tradeBadgeSeen && !active && !showCount && !showProPill;

            // b17e: NO pill background on the active tab. The active state is a
            // strong neon-green glow puddle UNDERNEATH the icon+label
            // (.tabbar-item__glow), the icon/label go brand green, and the
            // body lifts 2pt. Inactive icon/label are metallic greys with a
            // 1px white bevel highlight. All driven by data-active + the
            // .tabbar-item__* classes in tokens.css so the colours track theme.
            // b19-ui (E31): each tab is now a direct child of the full-width row
            // and is an equal-basis flex column (flex-1 basis-0) so the five
            // slots distribute evenly across any device width. The top offset
            // (matches HeroDexButton marginTop) and the home-indicator safe-area
            // floor (previously carried by an outer flanking-column wrapper)
            // move onto the tab itself now that the wrapper is gone.
            const itemClass = [
              "tabbar-item flex-1 basis-0 min-w-0 flex flex-col items-center justify-center gap-1 px-1",
              "transition-all [transition-duration:var(--duration-fast)] ease-out",
              "relative no-underline select-none",
            ].join(" ");
            // E35: the bar is 20% shorter. Each item is a fixed-height flex
            // column (BAR_CONTENT_HEIGHT_PX) with justify-center, so icon +
            // label stay vertically centred and fully visible against the
            // shorter bar.
            // E37: the items previously sat too high off the bottom edge
            // because each item carried the FULL home-indicator safe-area inset
            // as bottom padding (stacked on the nav's own 0.5x inset). The item
            // now keeps only ITEM_BOTTOM_INSET_FRACTION of the inset, which
            // pulls the icon + label closer to the bottom edge while still
            // clearing the home indicator, and frees top room so the centred
            // content rises.
            const itemStyle = {
              height: `${BAR_CONTENT_HEIGHT_PX}px`,
              paddingBottom: `calc(var(--nx-safe-bottom) * ${ITEM_BOTTOM_INSET_FRACTION})`,
            };

            const inner = (
              <>
                {/* Neon-green glow puddle under the active icon (no pill). */}
                <span className="tabbar-item__glow" aria-hidden />
                <div className="tabbar-item__body flex flex-col items-center gap-1">
                  <div className="relative flex items-center justify-center">
                    <Icon
                      className="tabbar-item__icon w-[18px] h-[18px] transition-transform duration-200"
                      strokeWidth={active ? 2.5 : 2}
                    />
                    {showProPill && (
                      <span
                        className="tabbar-new-badge absolute -top-1.5 -right-3 px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wide uppercase leading-none scale-90"
                        data-testid={`${testid}-pro-badge`}
                        aria-label="Pro feature"
                      >
                        PRO
                      </span>
                    )}
                    {showBadge && (
                      <span className="tabbar-new-badge absolute -top-1.5 -right-3 px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wide uppercase leading-none scale-90">
                        NEW
                      </span>
                    )}
                    {showCount && (
                      <span
                        className="absolute -top-2 -right-2.5 min-w-[15px] h-[15px] px-1 rounded-full flex items-center justify-center bg-[var(--accent-fill)] text-[var(--text-on-accent)] text-[9px] font-black leading-none tabular-nums shadow-sm"
                        data-testid="tab-friends-pending-badge"
                        aria-label={`${friendCount} pending friend request${friendCount === 1 ? "" : "s"}`}
                      >
                        {friendCount > 9 ? "9+" : friendCount}
                      </span>
                    )}
                  </div>
                  {/* Tight single-word label for the hardware-console layout.
                      E54: the old `tracking-widest` (0.1em) overflowed the
                      narrow flex-1 column for the longest label ("CALENDAR") at
                      390pt/320pt, clipping its last letter. Cap the letter-
                      spacing at TAB_LABEL_TRACKING_EM (a relative em, fluid - not
                      a 390pt hardcode) and force a single centred non-wrapping
                      line in a min-w-0 column so every label (PROFILE / FRIENDS /
                      TRADE / CALENDAR) renders in full at all widths. */}
                  <span
                    className="tabbar-item__label block w-full text-[9px] font-semibold uppercase text-center whitespace-nowrap scale-95"
                    style={{ letterSpacing: `${TAB_LABEL_TRACKING_EM}em` }}
                  >
                    {label.split(" ")[0]}
                  </span>
                </div>
              </>
            );

            // Long-press wiring for the user-choice slot. On the Link variant
            // the suppressed tap must also preventDefault or wouter navigates.
            const slotPressProps = slotChooser
              ? {
                  onPointerDown: startChooserPress,
                  onPointerUp: cancelChooserPress,
                  onPointerLeave: cancelChooserPress,
                  onPointerCancel: cancelChooserPress,
                  onContextMenu: (e: { preventDefault: () => void }) => e.preventDefault(),
                }
              : {};
            const slotStyle = slotChooser
              ? { ...itemStyle, WebkitTouchCallout: "none" as const }
              : itemStyle;

            if (picker) {
              // Friend-scoped option (Compare / Trade): a button, not a route.
              return (
                <button
                  key={testid}
                  type="button"
                  data-testid={testid}
                  data-active={active ? "true" : undefined}
                  onClick={() => {
                    if (suppressTapRef.current) {
                      suppressTapRef.current = false;
                      return;
                    }
                    setFriendPicker(picker);
                  }}
                  className={itemClass}
                  style={slotStyle}
                  {...slotPressProps}
                >
                  {inner}
                </button>
              );
            }

            return (
              <Link
                key={href}
                href={href}
                data-testid={testid}
                data-active={active ? "true" : undefined}
                onClick={(e) => {
                  if (suppressTapRef.current) {
                    suppressTapRef.current = false;
                    e.preventDefault();
                    return;
                  }
                  // The current page may veto leaving (species-page Manual
                  // Edit with an unresolved regional answer); the guard
                  // surfaces its own sheet when it blocks.
                  if (!confirmPageExit()) {
                    e.preventDefault();
                    return;
                  }
                  if (href === "/friends") markTradeBadgeSeen();
                }}
                className={itemClass}
                style={slotStyle}
                {...slotPressProps}
              >
                {inner}
              </Link>
            );
  }
}